import React, { useCallback, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, SlidersHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@exiftrator/shared/components/ui';
import {
  countActiveFileListFilters,
  createDefaultFileListFilters,
  FILE_LIST_FILTER_OPTIONS,
  FILE_LIST_SORT_FIELDS,
  FILE_LIST_SORT_OPTIONS,
  normalizeFileListFilters,
  normalizeFileListSort,
} from '../../config/fileListView';
import { cn } from '../utils/cn';
import { HeaderActionButton } from '../Header/HeaderActions';

function toggleFilterValue(values, value) {
  if (values.includes(value)) {
    return values.filter((entry) => entry !== value);
  }
  return [...values, value];
}

function SortDirectionIcon({ direction }) {
  if (direction === 'desc') {
    return <ArrowDown className="size-3.5" />;
  }
  return <ArrowUp className="size-3.5" />;
}

export function FileListControls({
  sort,
  filters,
  onSortChange,
  onFiltersChange,
  className,
}) {
  const normalizedSort = useMemo(() => normalizeFileListSort(sort), [sort]);
  const normalizedFilters = useMemo(
    () => normalizeFileListFilters(filters),
    [filters],
  );
  const activeFilterCount = useMemo(
    () => countActiveFileListFilters(normalizedFilters),
    [normalizedFilters],
  );

  const [menuOpen, setMenuOpen] = useState(false);

  const sortLabel = useMemo(() => {
    return (
      FILE_LIST_SORT_OPTIONS.find((option) => option.id === normalizedSort.field)
        ?.label || 'Import order'
    );
  }, [normalizedSort.field]);

  const updateSort = useCallback(
    (nextSort) => {
      onSortChange?.(normalizeFileListSort(nextSort));
    },
    [onSortChange],
  );

  const handleSortFieldChange = useCallback(
    (field) => {
      const nextSort =
        field === FILE_LIST_SORT_FIELDS.IMPORT
          ? { field, direction: 'asc' }
          : { field, direction: normalizedSort.direction };
      updateSort(nextSort);
    },
    [normalizedSort.direction, updateSort],
  );

  const handleSortDirectionChange = useCallback(
    (direction) => {
      updateSort({ ...normalizedSort, direction });
    },
    [normalizedSort, updateSort],
  );

  const handleMenuOpenChange = useCallback((open) => {
    setMenuOpen(open);
  }, []);

  const toggleFilter = useCallback(
    (group, value) => {
      const nextFilters = {
        ...normalizedFilters,
        [group]: toggleFilterValue(normalizedFilters[group], value),
      };
      onFiltersChange?.(normalizeFileListFilters(nextFilters));
    },
    [normalizedFilters, onFiltersChange],
  );

  const resetFilters = useCallback(() => {
    onFiltersChange?.(createDefaultFileListFilters());
  }, [onFiltersChange]);

  const buttonLabel = activeFilterCount > 0
    ? `Sort & Filter (${activeFilterCount})`
    : 'Sort & Filter';

  return (
    <div className={cn('flex items-center', className)}>
      <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <HeaderActionButton intent="utility" size="sm" className="whitespace-nowrap">
            <SlidersHorizontal className="size-3.5" />
            {buttonLabel}
            {normalizedSort.field !== FILE_LIST_SORT_FIELDS.IMPORT && (
              <SortDirectionIcon direction={normalizedSort.direction} />
            )}
          </HeaderActionButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-text-tertiary">
            {`Sort: ${sortLabel}`}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={normalizedSort.field}
            onValueChange={handleSortFieldChange}
          >
            {FILE_LIST_SORT_OPTIONS.map((option) => (
              <DropdownMenuRadioItem key={option.id} value={option.id}>
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>

          {normalizedSort.field !== FILE_LIST_SORT_FIELDS.IMPORT && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-text-tertiary">
                Direction
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={normalizedSort.direction}
                onValueChange={handleSortDirectionChange}
              >
                <DropdownMenuRadioItem value="asc">Ascending</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="desc">Descending</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-text-tertiary">
            Date status
          </DropdownMenuLabel>
          {FILE_LIST_FILTER_OPTIONS.dateStatuses.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.id}
              checked={normalizedFilters.dateStatuses.includes(option.id)}
              onCheckedChange={() => toggleFilter('dateStatuses', option.id)}
              onSelect={(event) => event.preventDefault()}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))}

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-text-tertiary">
            GPS status
          </DropdownMenuLabel>
          {FILE_LIST_FILTER_OPTIONS.gpsStatuses.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.id}
              checked={normalizedFilters.gpsStatuses.includes(option.id)}
              onCheckedChange={() => toggleFilter('gpsStatuses', option.id)}
              onSelect={(event) => event.preventDefault()}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))}

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-text-tertiary">
            Edit status
          </DropdownMenuLabel>
          {FILE_LIST_FILTER_OPTIONS.editStatuses.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.id}
              checked={normalizedFilters.editStatuses.includes(option.id)}
              onCheckedChange={() => toggleFilter('editStatuses', option.id)}
              onSelect={(event) => event.preventDefault()}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))}

          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(event) => {
            event.preventDefault();
            resetFilters();
          }}>
            Reset
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default FileListControls;
